﻿import Application from '@core/classes/Applications/Application';
import CustomerRecord from '@core/classes/Customer/CustomerRecord';
import { CUSTOMER_APPLICATION_STATUS } from '@core/classes/Enums';
import api from '@core/services/api/_api';
import util from '@core/services/util';
import { formatDateToString, getDateInstance } from '@core/services/util.date';
import { differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale/es';
import settings from 'settings';
import { IStore } from './Customer/customer-helper';

export async function getCustomerApplication(customerId: string) {
	try {
		const customerAppData = await api.applications.getByCustomer(customerId);
		if (customerAppData?.status == 200 && customerAppData.data) {
			const customerApplication = new Application(customerAppData.data);
			return customerApplication;
		}
	} catch (error) {
		console.error('error', error);
	}
}

export const getPrefilledData: any = (dataToReplace: any, sourceData: any, question: any, language: any) => {
	dataToReplace.forEach((r: any) => {
		const formattedKey: any = '{' + r.key + '}';
		while (question.includes(formattedKey)) {
			const propertyValue: any = sourceData[r.prefilledPropertyName];
			if (propertyValue == null) {
				question = question.replace(formattedKey, '');
			} else {
				if (r.type == 'date') {
					let textWithReplacements = propertyValue;
					if (language == 'Spanish') {
						if (getDateInstance(textWithReplacements, true)?.getFullYear() >= 2000) {
							if (question.dateType == 'year') {
								textWithReplacements = formatDateToString(textWithReplacements, { formatString: 'yyyy', locale: es });
							}
							textWithReplacements = formatDateToString(textWithReplacements, { formatString: "d 'de' MMMM 'del' yyyy", locale: es });
						} else {
							textWithReplacements = formatDateToString(textWithReplacements, { formatString: "d 'de' MMMM 'de' yyyy", locale: es });
						}
					} else {
						textWithReplacements = formatDateToString(textWithReplacements, { formatString: 'MMMM do, yyyy' });
					}
					question = question.replace(formattedKey, textWithReplacements);
				} else {
					question = question.replace(formattedKey, propertyValue);
				}
			}
		}
	});
	return question;
};

export function hasCustomerApplication(customerApp: Application = null) {
	if (customerApp != null) {
		return !!customerApp?.id;
	} else return false;
}

export function hasCustomerApplicationBeenStarted(customerApp: Application = null) {
	if (customerApp != null) {
		return !!customerApp?.id && customerApp?.status > CUSTOMER_APPLICATION_STATUS.Created;
	} else return false;
}

export const isCustomerApplicationPartiallyCompleted = (customerApp: Application) => {
	if (customerApp != null) {
		if (customerApp.status == CUSTOMER_APPLICATION_STATUS.PartiallyCompleted) {
			return true;
		} else return false;
	}
};

export function isCustomerApplicationComplete(customerApp: Application = null) {
	if (customerApp != null) {
		return customerApp?.status == CUSTOMER_APPLICATION_STATUS.Completed || customerApp?.status == CUSTOMER_APPLICATION_STATUS.CompletedNoCredit;
	} else return false;
}

export function isCreditApplicationComplete(customerApp: Application = null) {
	if (customerApp != null) {
		return customerApp?.status == CUSTOMER_APPLICATION_STATUS.Completed;
	} else return false;
}

export function isEditApplicationPastDayLimit(customerApp: Application) {
	if (!customerApp?.dateCompleted) return false;

	const daysCount = differenceInDays(getUtcDate(), customerApp.dateCompleted);
	return daysCount >= 160; // If its past 6 months
}

export const isEditApplicationPastDayLimitByGlobalSettingsCheck = (customerApp: Application) => {
	if (!customerApp?.dateCompleted) return false;

	const daysCount = differenceInDays(getDateInstance(), customerApp.dateCompleted);
	const currentExpirationDateFromSettings = settings.lookups.customerApplicationEditPeriod;
	return daysCount >= currentExpirationDateFromSettings ? true : false;
};

export const isCustomerApplicationExpiredByGlobalSettingsCheck = (dateCompleted: any) => {
	if (dateCompleted) {
		const daysCount = differenceInDays(getDateInstance(), dateCompleted);
		const currentExpirationDateFromSettings = settings.lookups.customerApplicationExpiration;
		return daysCount >= currentExpirationDateFromSettings ? true : false;
	} else return false;
};

export const isCustomerApplicationExpired = async (customerApp: Application) => {
	try {
		const response = await api.applications.checkIfApplicationIsExpired(customerApp);
		return !!response?.data;
	} catch (error) {
		util.toastr('error', 'Error', 'Please try again');
		console.error(error);
	}
};

export const checkIfWeNeedANewApplication = async (currentCustomerApplication: Application, customer: any, store: IStore, storeCode: string = null) => {
	if (!currentCustomerApplication || isCustomerApplicationExpiredByGlobalSettingsCheck(currentCustomerApplication.dateCompleted)) {
		try {
			const currentStoreCode = store?.storeCode ?? storeCode ?? null;
			if (currentStoreCode == null) return null;

			const customerApp = await createCustomerApplicationFromCustomer(currentCustomerApplication, customer, false, currentStoreCode);
			if (customerApp != null) {
				util.toastr('success', 'Success', 'Successfully sent.');
				return customerApp;
			}
		} catch (error) {
			util.toastr('error', 'Error', 'Error sending link to customer. Try again.');
		}
	}
};

export async function createCustomerApplicationFromCustomer(
	customerApp: Application,
	customer: CustomerRecord,
	isInCustomerPortal: boolean = false,
	storeCode: string = null,
) {
	if (!customerApp || isCustomerApplicationExpiredByGlobalSettingsCheck(customerApp.dateCompleted)) {
		try {
			const response = await api.applications.createApplication({
				prefilledData: {
					language: customer.personalInfo?.language ?? 'English',
					firstName: customer.personalInfo?.firstName,
					lastName: customer.personalInfo?.lastName,
					dateOfBirth: customer.personalInfo?.dob,
					phoneNumber: customer.personalInfo?.cell,
					emailAddress: customer.personalInfo.email,
					storeCode: storeCode ? storeCode : customer.storeCodes[0],
				},
				customerId: customer.id,
				isEntity: false, // we don't create business applications anymore
				isInCustomerPortal: isInCustomerPortal,
			});

			if (response.data) {
				customerApp = new Application(response.data);
				return customerApp;
			}
		} catch (err) {
			console.error(err, 'Error Creating Customer Application');
		}
	}
}

export async function completeCustomerApplication(customerApp: Application) {
	// This is to pick up where you left off.
	// const pathFollowed: [] = customerApp.quoteAppData?.pathFollowed;
	// const questionKey: string =  pathFollowed[pathFollowed.length - 1];

	const questionKey =
		customerApp.quoteAppData?.applicationsDataAnswered?.personalInfo?.currentResidence?.residenceStatus?.status != null
			? 'currentlyEmployed'
			: 'provideEstimateCreditScore';
	await editCustomerApplicationSection(customerApp, questionKey, true);
}

export async function getAccurateQuoteForCustomerApplication(isCustomerAnEntity: boolean, customerApp: Application) {
	const questionKey =
		customerApp.quoteAppData?.applicationsDataAnswered?.personalInfo?.currentResidence?.residenceStatus?.status != null
			? 'currentlyEmployed'
			: 'provideEstimateCreditScore';
	await editCustomerApplicationSection(customerApp, questionKey, true);
}

export async function editPersonalInfoSectionOnCustomerApplication(isCustomerAnEntity: boolean, customerApp: Application) {
	const editSectionQuestionKey: 'provideCompanyLegalName' | 'providePrefix' = isCustomerAnEntity ? 'provideCompanyLegalName' : 'providePrefix';
	await editCustomerApplicationSection(customerApp, editSectionQuestionKey);
}

export async function editSSNOnCustomerApplication(customerApp: Application) {
	const editSectionQuestionKey: string = 'wantAccureQuoteSSN';
	const isSingleQuestionEdit = true;
	await editCustomerApplicationSection(customerApp, editSectionQuestionKey, false, false, isSingleQuestionEdit);
}
export async function editHousingSectionOnCustomerApplication(customerApp: Application) {
	await editCustomerApplicationSection(customerApp, 'provideMovedInDate');
}

export async function editEmploymentOnCustomerApplication(customerApp: Application) {
	await editCustomerApplicationSection(customerApp, 'currentlyEmployed');
}

async function editCustomerApplicationSection(
	customerApp: Application,
	questionKey: string,
	resetDateCompleted: boolean = false,
	isInStore: boolean = false,
	isSingleQuestionEdit: boolean = null,
) {
	const link = await generateApplicationEditLink(customerApp, questionKey, resetDateCompleted, isInStore, isSingleQuestionEdit);
	window.location.href = link; // opens in the current tab
}

/**
 * @param {Application} customerApp
 * @param {string} questionKey
 * @param {boolean} resetDateCompleted
 * @param {string} applicationId
 * @param {boolean} isInStore
 * @param {boolean} isSingleQuestionEdit - nullable
 * @returns A link for the customer to finish the credit app of their application
 */
export async function generateApplicationEditLink(
	customerApp: Application,
	questionKey: string,
	resetDateCompleted = false,
	isInStore = false,
	isSingleQuestionEdit = null as boolean,
) {
	const storeCode: string = customerApp.storeCode;
	const applicationId: string = customerApp.id;

	const response = await api.applications.generateApplicationEditLink({
		questionKey,
		resetDateCompleted,
		applicationId: applicationId,
		storeCode: storeCode,
		isInStore: isInStore,
		isSingleQuestionEdit,
	});

	return response.data;
}

const CustomerApplicationHelper = {
	getPrefilledData,
	getCustomerApplication,
	hasCustomerApplication,
	generateApplicationEditLink,
	completeCustomerApplication,
	isCreditApplicationComplete,
	editSSNOnCustomerApplication,
	isCustomerApplicationExpired,
	isEditApplicationPastDayLimit,
	isCustomerApplicationComplete,
	hasCustomerApplicationBeenStarted,
	editEmploymentOnCustomerApplication,
	createCustomerApplicationFromCustomer,
	getAccurateQuoteForCustomerApplication,
	isCustomerApplicationPartiallyCompleted,
	editHousingSectionOnCustomerApplication,
	editPersonalInfoSectionOnCustomerApplication,
	isCustomerApplicationExpiredByGlobalSettingsCheck,
	isEditApplicationPastDayLimitByGlobalSettingsCheck,
};

export default CustomerApplicationHelper;
function getUtcDate(): string | number | Date {
	throw new Error('Function not implemented.');
}
